import * as Sentry from "@sentry/react";
import { Duration } from "luxon";

import { recordEvent } from "@/services/telemetry";

const RELOAD_GUARD_KEY = "vitePreloadErrorLastReload";
// Suppress repeated auto-reloads to avoid a tight refresh loop when the
// chunk is genuinely unreachable (e.g. CDN outage, extension blocking).
const RELOAD_COOLDOWN = Duration.fromObject({ seconds: 30 });

type PreloadErrorDeps = {
  reload: () => void;
  now: () => number;
  storage: Pick<Storage, "getItem" | "setItem">;
  /** Recoverable path (auto-reload will likely fix the stale chunk): Honeycomb reliability event. */
  reportRecoverable: (context: Record<string, unknown>) => void;
  /** Unrecoverable path (a reload already failed to fix it): page Sentry with the real error. */
  reportUnrecoverable: (error: unknown, context: Record<string, unknown>) => void;
};

function buildDefaultDeps(): PreloadErrorDeps {
  return {
    reload: () => window.location.reload(),
    now: () => Date.now(),
    storage: window.sessionStorage,
    reportRecoverable: (context) => {
      recordEvent("chunk.preload_failed", { chunk: context });
    },
    reportUnrecoverable: (error, context) => {
      Sentry.captureException(error, { tags: { kind: "vite_preload_error" }, extra: context });
    },
  };
}

/**
 * Recovers from `Failed to fetch dynamically imported module` errors emitted
 * by Vite when a code-split chunk referenced by the cached HTML no longer
 * exists on the CDN (typically after a deploy).
 *
 * Reloads the page so the browser pulls a fresh `index.html` that references
 * the current chunk hashes. The cooldown prevents an infinite refresh loop
 * when reloading does not resolve the failure.
 */
export function handleVitePreloadError(
  event: VitePreloadErrorEvent,
  deps: PreloadErrorDeps = buildDefaultDeps(),
): boolean {
  const lastReload = Number(deps.storage.getItem(RELOAD_GUARD_KEY) ?? 0);
  const elapsed = deps.now() - lastReload;
  const withinCooldown = Number.isFinite(lastReload) && lastReload > 0 && elapsed < RELOAD_COOLDOWN.toMillis();

  const context = {
    moduleUrl: extractModuleUrl(event.payload),
    withinCooldown,
    msSinceLastReload: lastReload > 0 ? elapsed : null,
  };
  deps.reportRecoverable(context);

  if (withinCooldown) {
    // A reload inside the cooldown already failed to fix this chunk — the auto-recovery is
    // exhausted and the user is stuck on a broken page. This is the unrecoverable case.
    deps.reportUnrecoverable(event.payload, context);
    return false;
  }

  event.preventDefault();
  deps.storage.setItem(RELOAD_GUARD_KEY, String(deps.now()));
  deps.reload();
  return true;
}

export function initializePreloadErrorHandler(): void {
  window.addEventListener("vite:preloadError", (event) => {
    handleVitePreloadError(event);
  });
}

function extractModuleUrl(error: unknown): string | undefined {
  if (!(error instanceof Error)) return undefined;
  const match = error.message.match(/https?:\/\/\S+/);
  return match?.[0];
}
